import React, { useState } from "react";
import { ErrorBoundary } from "react-error-boundary";
import { Link } from "react-router-dom";

import {
  ClassificationIcon,
  FileSize,
  Grid,
  NoData,
  RenderWhen
} from "../../../../../../components";
import PreviewVisualizationModal from "../../../../../../components/PreviewVisualizationModal";
import Typography from "../../../../../../components/typography/Typography";
import useModal from "../../../../../../hooks/useModal";
import { getClassificationByLabels } from "../../../../../../lib/enums/ThreatStatusEnum";
import commonStyles from "../../../../containers/report_summary/report-summary.module.scss";
import { pathToSS } from "../../../../util";
import { useEmailAttachments } from "../../hooks";
import styles from "./email.module.scss";
import { useLocalSampleDetails } from "../../../../hooks";

const EmailAttachmentError = () => (
  <span className={commonStyles.error_msg}>
    Error loading email attachments
  </span>
);

const EmailAttachmentNoData = () => (
  <span className={commonStyles.error_msg}>No available email attachments</span>
);

type AttachmentData = {
  attachment_name: string;
  file_type: string;
  file_subtype: string;
  type_display: string;
  size: number;
  path: string | undefined;
  sha1: string;
  relationship_id: number | undefined;
  classification: string;
};

const EmailAttachmentGridSchema = (
  setCurrentPreviewData: React.Dispatch<object>,
  togglePreviewModal: () => void,
  sourceID?: number
) => ({
  fields: [
    {
      name: "attachment_name",
      displayTitle: "Attachment Name",
      sortable: false,
      component: (data: AttachmentData) => (
        <Link
          to={pathToSS(data.sha1, sourceID, "", data.relationship_id)}
          className={styles.grid_element_with_classification}
        >
          <ClassificationIcon
            classification={data.classification}
            classificationStyle={{
              flexShrink: 0
            }}
          />
          <Typography
            variant={"body1"}
            className={`${styles.item_link} overflow-ellipsis`}
          >
            {" "}
            {data?.attachment_name}{" "}
          </Typography>
        </Link>
      ),
      style: { width: "40%" }
    },
    {
      name: "file_type",
      displayTitle: "File Type",
      sortable: false,
      component: (data: AttachmentData) => (
        <Typography variant={"body1"}> {data?.file_type} </Typography>
      ),
      style: { width: "30%" }
    },
    {
      name: "size",
      displayTitle: "Size",
      sortable: true,
      component: (data: AttachmentData) => (
        <FileSize value={data?.size ?? <NoData />} />
      ),
      style: { width: "15%" }
    },
    {
      name: "preview",
      sortable: false,
      component: (data: AttachmentData) => {
        if (!data) return <NoData />;
        return (
          <a
            onClick={() => {
              setCurrentPreviewData({
                sha1: data.sha1,
                sourceId: sourceID,
                relationshipId: data.relationship_id,
                fileType: data.file_type,
                fileSubtype: data.file_subtype,
                typeDisplay: data.type_display,
                threatStatus: getClassificationByLabels(data.classification)
              });
              togglePreviewModal();
            }}
          >
            <Typography
              variant={"body1"}
              className={`${styles.item_link} overflow-ellipsis`}
            >
              Preview
            </Typography>
          </a>
        );
      },
      style: { width: "15%", textAlign: "center" }
    }
  ]
});

interface EmailAttachmentsProps {
  hashValue: string;
  sourceID: number | undefined;
  relationshipID: number;
  attachmentsRoute: string;
}
const EmailAttachmentsGrid: React.FC<EmailAttachmentsProps> = ({
  hashValue,
  sourceID,
  attachmentsRoute,
  relationshipID
}) => {
  const { data: localData } = useLocalSampleDetails(
    hashValue,
    sourceID,
    relationshipID
  );
  const sourceHash = localData?.sample_source_sha1;
  const { results } = useEmailAttachments(
    sourceHash || hashValue,
    relationshipID
  );
  const [previewModalShow, , togglePreviewModal] = useModal();
  const [currentPreviewData, setCurrentPreviewData] = useState({
    sha1: "",
    sourceId: 0,
    relationshipId: undefined,
    fileType: "",
    fileSubtype: "",
    typeDisplay: "",
    threatStatus: 0
  });

  return (
    <>
      <ErrorBoundary fallback={<EmailAttachmentError />}>
        <div className={styles.attachments_grid}>
          <Grid
            schema={EmailAttachmentGridSchema(
              setCurrentPreviewData,
              togglePreviewModal,
              sourceID
            )}
            data={results.slice(0, 4)}
            emptyGridMsg={<EmailAttachmentNoData />}
            showLoading={false}
            hidePaging
          />
          {results?.length > 4 && (
            <Typography variant={"body1"} className={styles.more_attachments}>
              <Link className={commonStyles.link} to={attachmentsRoute}>
                +{results.length - 4} More Attachments
              </Link>
            </Typography>
          )}
        </div>
      </ErrorBoundary>
      <RenderWhen condition={previewModalShow}>
        <PreviewVisualizationModal
          sha1={currentPreviewData.sha1}
          fileType={currentPreviewData.fileType}
          fileSubtype={currentPreviewData.fileSubtype}
          typeDisplay={currentPreviewData.typeDisplay}
          sourceId={currentPreviewData.sourceId}
          threatStatus={currentPreviewData.threatStatus}
          relationshipId={currentPreviewData.relationshipId}
          onHide={togglePreviewModal}
        />
      </RenderWhen>
    </>
  );
};

export default EmailAttachmentsGrid;
